import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Info, Target } from 'lucide-react';
import {
  analisar,
  calcularCenarios,
  classificar,
  formatarPct,
  formatarReais,
  roiPessimista,
  type Cenario,
  type ContextoClassificacao,
  type CustosAquisicao,
  resolverTarifa,
  RECADO_SEM_TARIFA,
  ROTULO_TIPO_ANUNCIO,
  TIPOS_ANUNCIO,
  type CustosVenda,
  type EntradaAnalise,
  type TarifaMarketplace,
  type TipoAnuncio,
  custoEnvio,
  RECADO_SEM_FRETE,
  reputacaoDoNivelML,
  TABELA_FRETE_ML,
  ratearPorUnidade,
  RECADO_SEM_RATEIO,
  calcularReferencia,
  dispersaoBps,
} from '@profitlens/calculo';
import { Cabecalho } from '../componentes/Cabecalho';
import { Vazio } from '../componentes/Estados';
import { Campo, CampoPercentual, CampoQuantidade, CampoReais } from '../componentes/Campos';
import { CartaoClassificacao } from '../componentes/Classificacao';
import { GradeIndicadores, Indicador } from '../componentes/Indicador';
import { Reais } from '../componentes/Valor';
import { useAuth } from '../contextos/AuthContext';
import { useProdutos } from '../servicos/produtos';
import { useFornecedores } from '../servicos/fornecedores';
import { hojeISO, useRespostasDoProduto } from '../servicos/cotacoes';
import {
  criteriosEmVigor,
  usePerfisCusto,
  useCriterios,
  useCustosFixos,
} from '../servicos/configuracoes';
import { useCategorias } from '../servicos/categorias';
import { useAnuncios } from '../servicos/mercado';
import { criarOportunidade } from '../servicos/oportunidades';
import { statusConexaoML, useTarifaML, type StatusConexao } from '../servicos/mercadolivre';
import type { PerfilCusto } from '../tipos';

/**
 * A tela que responde "isto se paga?".
 *
 * O motor já existe e está verificado por 100 testes — aqui só se monta a
 * entrada e se mostra o resultado. Duas decisões de tela que valem explicar:
 *
 * 1. **A conta refaz a cada tecla.** Não há botão "calcular". A pergunta que
 *    essa tela responde raramente é "quanto dá?", e quase sempre "a partir de
 *    quanto para de valer?" — e essa você só responde mexendo no número e
 *    vendo a classificação virar.
 *
 * 2. **A classificação nunca aparece sozinha** (RN-093). Vêm junto todos os
 *    critérios avaliados, atendidos e não atendidos, com o identificador da
 *    regra. Dá para discordar do sistema com argumento.
 */

/** Custos de aquisição que não vêm da cotação e o usuário informa à parte. */
interface ExtrasAquisicao {
  freteEntradaCents: number;
  impostosAquisicaoCents: number;
  seguroCents: number;
  inspecaoCents: number;
  armazenagemEntradaCents: number;
  outrosAquisicaoCents: number;
}

const EXTRAS_ZERADOS: ExtrasAquisicao = {
  freteEntradaCents: 0,
  impostosAquisicaoCents: 0,
  seguroCents: 0,
  inspecaoCents: 0,
  armazenagemEntradaCents: 0,
  outrosAquisicaoCents: 0,
};

/**
 * Monta os custos de venda: o perfil dá o que é da SUA operação, a consulta ao
 * Mercado Livre dá o que o marketplace cobra.
 *
 * A comissão e a tarifa fixa não vêm do perfil porque dependem da categoria E
 * DO PREÇO — a mesma categoria cobra percentuais diferentes em faixas de preço
 * diferentes. Sem tarifa consultada, entram `null`, e a análise sai como
 * DADOS_INSUFICIENTES dizendo o que falta. Nunca zero.
 */
const vendaDoPerfil = (
  p: PerfilCusto | undefined,
  tarifa: TarifaMarketplace | null,
  freteCents: number | null,
  rateioFixoCents: number,
): CustosVenda => ({
  comissaoBps: tarifa?.percentualBps ?? null,
  tarifaFixaCents: tarifa?.fixoCents ?? null,
  impostoVendaBps: p?.impostoVendaBps ?? null,
  devolucaoBps: p?.devolucaoBps ?? null,
  custoFinanceiroBps: p?.custoFinanceiroBps ?? null,
  // O Custo dos Envios calculado manda; o valor do perfil é só a reserva para
  // quando o produto não tem peso nem dimensão.
  freteSubsidiadoCents: freteCents ?? p?.freteSubsidiadoCents ?? 0,
  // O custo fixo rateado entra aqui: ele e por unidade vendida, igual embalagem
  // e expedicao. Some-lo separado seria criar duas linhas para a mesma natureza.
  custosVendaFixosCents: (p?.custosVendaFixosCents ?? 0) + rateioFixoCents,
  custosVendaAntecipadosCents: p?.custosVendaAntecipadosCents ?? 0,
});

const diasEntre = (iso: string | null, hoje: string): number | null => {
  if (!iso) return null;
  const a = Date.parse(`${iso}T00:00:00Z`);
  const b = Date.parse(`${hoje}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.max(0, Math.round((b - a) / 86_400_000));
};

export function Viabilidade() {
  const { produtos } = useProdutos();
  const { fornecedores } = useFornecedores();
  const { perfis } = usePerfisCusto();
  const { criterios } = useCriterios();

  const [produtoId, setProdutoId] = useState('');
  const [respostaId, setRespostaId] = useState('');
  const [perfilId, setPerfilId] = useState('');
  const [quantidadeMilli, setQuantidadeMilli] = useState<number | null>(null);
  const [precoVendaCents, setPrecoVendaCents] = useState<number | null>(null);
  const [precoFornecedorManualCents, setPrecoFornecedorManualCents] = useState<number | null>(null);
  const [extras, setExtras] = useState<ExtrasAquisicao>(EXTRAS_ZERADOS);
  // Clássico é o ponto de partida: cobra menos e é o anúncio mais comum.
  const [tipoAnuncio, setTipoAnuncio] = useState<TipoAnuncio>('gold_special');

  const { respostas } = useRespostasDoProduto(produtoId || null);
  const { categorias } = useCategorias();

  const produto = produtos.find((p) => p.id === produtoId);
  const perfilPadrao = perfis.find((p) => p.padrao);
  const perfil = perfis.find((p) => p.id === perfilId) ?? perfilPadrao;
  const resposta = respostas.find((r) => `${r.cotacaoId}-${r.id}` === respostaId);

  const nomeFornecedor = useMemo(
    () =>
      Object.fromEntries(fornecedores.map((f) => [f.id, f.nomeFantasia || f.razaoSocial])) as Record<
        string,
        string
      >,
    [fornecedores],
  );

  // Escolher o produto propõe a cotação mais barata e a quantidade mínima dela:
  // é o ponto de partida que quase sempre se quer, e continua editável.
  useEffect(() => {
    if (respostas.length === 0) {
      setRespostaId('');
      return;
    }
    const comPreco = respostas.filter((r) => r.precoUnitarioCents !== null);
    if (comPreco.length === 0) return;
    const maisBarata = comPreco.reduce((a, b) =>
      (a.precoUnitarioCents ?? 0) <= (b.precoUnitarioCents ?? 0) ? a : b,
    );
    setRespostaId(`${maisBarata.cotacaoId}-${maisBarata.id}`);
    setQuantidadeMilli((q) => q ?? maisBarata.quantidadeMinimaMilli ?? 1_000);
  }, [produtoId, respostas.length]);

  useEffect(() => {
    if (!perfilId && perfilPadrao) setPerfilId(perfilPadrao.id);
  }, [perfilPadrao?.id]);

  const precoFornecedorCents =
    resposta?.precoUnitarioCents ?? precoFornecedorManualCents ?? null;

  /**
   * A tarifa do marketplace vem de consulta, para a categoria e o preço EXATOS
   * desta análise — ela muda de faixa conforme o preço, então perguntar uma vez
   * e reaproveitar daria o número errado assim que o preço mudasse.
   */
  const categoria = categorias.find((c) => c.id === produto?.categoriaId);
  const { tarifas, consultando: consultandoTarifa, erro: erroTarifa } = useTarifaML(
    categoria?.mlCategoriaId,
    precoVendaCents,
  );
  const tarifaDoTipo = tarifas?.[tipoAnuncio] ?? null;

  /**
   * Falta tarifa por três motivos diferentes, e cada um se resolve num lugar:
   * produto sem categoria, na ficha da mercadoria; categoria sem vínculo com o
   * Mercado Livre, em Configurações; consulta não feita, tentando de novo.
   * Dizer só "falta comissão" mandaria a pessoa procurar no lugar errado.
   */
  const tarifaResolvida = resolverTarifa(categoria, tarifaDoTipo);

  /**
   * O custo fixo diluido por unidade. Aluguel e salario existem mesmo sem
   * vender, e deixa-los de fora faz a margem parecer melhor do que e.
   */
  /**
   * O outro lado da conta: por quanto o mercado vende. Sem isso a análise sabe
   * quanto custa comprar e não sabe se dá para vender.
   */
  const { anuncios } = useAnuncios(produtoId || null);
  const precosDeMercado = useMemo(
    () =>
      anuncios
        .map((a) => a.precoUnitarioNormCents)
        .filter((p): p is number => typeof p === 'number' && p > 0),
    [anuncios],
  );

  // Atenção ao nome: `perfil` nesta tela é o perfil de CUSTO. Quem está usando o
  // sistema é `usuario` — trocar os dois gravaria o id errado como autor.
  const { perfil: usuario, podeEscrever } = useAuth();
  const [guardando, setGuardando] = useState(false);
  const [guardada, setGuardada] = useState<string | null>(null);
  const [erroGuardar, setErroGuardar] = useState<string | null>(null);

  const { custos: custosFixos } = useCustosFixos();
  const rateio = useMemo(
    () => ratearPorUnidade(custosFixos, criterios?.vendasMensaisEsperadas ?? null),
    [custosFixos, criterios?.vendasMensaisEsperadas],
  );

  // A reputação sai da conta conectada quando existe — é ela que multiplica o
  // Custo dos Envios, e vinda da API deixa de ser um campo que alguém escolheu.
  const [conexao, setConexao] = useState<StatusConexao | null>(null);
  useEffect(() => {
    statusConexaoML().then(setConexao).catch(() => setConexao(null));
  }, []);
  const reputacaoDaConta = reputacaoDoNivelML(conexao?.reputacaoNivel);

  /**
   * O Custo dos Envios sai do peso CONSIDERADO — o maior entre o real e o
   * cubado. Um produto leve e volumoso paga pelo volume, e é justamente o caso
   * em que olhar só a balança subestima o custo.
   *
   * Repare que a tabela não tem coluna de destino: o vendedor paga o mesmo para
   * qualquer canto do país. O que muda o valor é peso, preço e reputação.
   */
  const frete = useMemo(() => {
    if (!produto || precoVendaCents === null) return null;
    return custoEnvio(TABELA_FRETE_ML, {
      pesoRealGramas: produto.pesoGramas,
      comprimentoMm: produto.comprimentoMm,
      larguraMm: produto.larguraMm,
      alturaMm: produto.alturaMm,
      precoCents: precoVendaCents,
      // A reputação da CONTA manda quando ela está conectada: ela é fato, e o
      // campo do perfil é escolha — que pode estar desatualizada sem ninguém ver.
      reputacao: reputacaoDaConta ?? perfil?.reputacaoML ?? 'VERDE',
    });
  }, [produto, precoVendaCents, perfil?.reputacaoML, reputacaoDaConta]);

  const entrada: EntradaAnalise | null = useMemo(() => {
    if (!quantidadeMilli || quantidadeMilli <= 0) return null;
    if (precoFornecedorCents === null || precoVendaCents === null) return null;

    const aquisicao: CustosAquisicao = {
      precoFornecedorCents,
      ...extras,
      perdaBps: perfil?.perdaBps ?? 0,
    };
    return {
      quantidadeMilli,
      precoVendaCents,
      aquisicao,
      venda: vendaDoPerfil(
        perfil,
        tarifaResolvida.definida ? tarifaResolvida.tarifa : null,
        frete?.definido ? frete.custoCents : null,
        rateio.definido ? rateio.porUnidadeCents : 0,
      ),
      pctVendidoBps: 10_000,
      reservaSegurancaBps: 0,
    };
    // `tarifaDoTipo` PRECISA estar aqui: ela chega depois, por consulta. Sem ela
    // na lista, a tela mostrava a tarifa certa e a conta continuava usando a
    // ausência da primeira renderização — resultado certo ao lado de conta
    // errada, que é o pior jeito de errar.
  }, [quantidadeMilli, precoFornecedorCents, precoVendaCents, extras, perfil, tarifaDoTipo, frete, rateio]);

  const resultado = useMemo(() => (entrada ? analisar(entrada) : null), [entrada]);

  const cenarios: Cenario[] = useMemo(() => {
    if (!entrada) return [];
    return calcularCenarios(
      entrada,
      criterios
        ? {
            deltaPessimistaBps: criterios.deltaPessimistaBps,
            deltaOtimistaBps: criterios.deltaOtimistaBps,
            pctVendidoPessimistaBps: criterios.pctVendidoPessimistaBps,
          }
        : undefined,
    );
  }, [entrada, criterios]);

  const veredito = useMemo(() => {
    if (!resultado || !entrada) return null;
    /**
     * Os anúncios vêm da pesquisa de mercado, digitados por quem opera — a
     * coleta automática segue bloqueada (P1). Zero anúncios continua sendo uma
     * resposta legítima, e o classificador a transforma num critério não
     * atendido com o número da regra, nunca num número inventado.
     */
    const ctx: ContextoClassificacao = {
      qtdAnunciosValidos: precosDeMercado.length,
      menorAnuncioValidoCents: precosDeMercado.length > 0 ? Math.min(...precosDeMercado) : null,
      maiorAnuncioValidoCents: precosDeMercado.length > 0 ? Math.max(...precosDeMercado) : null,
      idadeCotacaoDias: diasEntre(resposta?.dataResposta ?? null, hojeISO()),
      estoqueDisponivelMilli: resposta?.quantidadeDisponivelMilli ?? null,
      prazoRetornoDias: null,
      roiPessimistaBps: roiPessimista(cenarios),
      giroEstoqueDias: null,
    };
    return classificar(resultado, criteriosEmVigor(criterios), ctx, entrada.quantidadeMilli);
  }, [resultado, entrada, resposta, cenarios, criterios, precosDeMercado]);

  if (produtos.length === 0) {
    return (
      <div className="flex flex-col gap-5">
        <Cabecalho titulo="Viabilidade" descricao="Se a operação se paga, e a partir de quanto." />
        <Vazio
          icone={<Calculator className="size-8" />}
          titulo="Nenhuma mercadoria cadastrada"
          descricao="A análise parte de um produto e de quanto ele custa. Cadastre a mercadoria e registre ao menos uma cotação."
          acao={
            <Link
              to="/mercadorias/nova"
              className="rounded bg-marca px-3 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90"
            >
              Cadastrar mercadoria
            </Link>
          }
        />
      </div>
    );
  }

  const semPerfil = perfis.length === 0;

  async function guardarOportunidade() {
    if (!usuario || !resultado?.completo || !entrada || !produto) return;
    setErroGuardar(null);
    setGuardando(true);
    try {
      const id = await criarOportunidade(
        {
          produtoId: produto.id,
          fornecedorId: resposta?.fornecedorId ?? null,
          classificacao: veredito?.classificacao ?? 'DADOS_INSUFICIENTES',
          lucroTotalCents: resultado.lucroTotalCents,
          roiBps: resultado.roiBps,
          retrato: {
            quantidadeMilli: entrada.quantidadeMilli,
            precoVendaCents: entrada.precoVendaCents,
            precoFornecedorCents: entrada.aquisicao.precoFornecedorCents,
            custoUnitarioEfetivoCents: resultado.custoUnitarioEfetivoCents,
            capitalNecessarioCents: resultado.capitalNecessarioCents,
            margemBps: resultado.margemBps,
            comissaoBps: entrada.venda.comissaoBps,
            freteCents: frete?.definido ? frete.custoCents : null,
            custoFixoRateadoCents: rateio.definido ? rateio.porUnidadeCents : 0,
            tipoAnuncio: ROTULO_TIPO_ANUNCIO[tipoAnuncio],
            anunciosComparaveis: precosDeMercado.length,
          },
        },
        usuario.id,
      );
      setGuardada(id);
    } catch (e) {
      setErroGuardar((e as { message?: string }).message ?? 'Não deu para guardar.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <Cabecalho titulo="Viabilidade" descricao="Se a operação se paga, e a partir de quanto." />

      {semPerfil && (
        <div className="rounded-md border-l-3 border-c-res bg-c-res/10 px-4 py-3 text-sm text-tinta-2">
          Não há <strong className="text-tinta">perfil de custo</strong> cadastrado. Sem ele não há
          comissão nem alíquota, e a análise não fecha a conta —{' '}
          <Link to="/configuracoes" className="text-marca underline-offset-2 hover:underline">
            cadastre um em Configurações
          </Link>
          .
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
        {/* ---------- entrada ---------- */}
        <form className="flex flex-col gap-4 rounded-md border border-regua-2 bg-superficie p-4">
          <Campo rotulo="Mercadoria">
            <select
              value={produtoId}
              onChange={(e) => {
                setProdutoId(e.target.value);
                setRespostaId('');
              }}
              className="w-full rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca"
            >
              <option value="">Escolha…</option>
              {produtos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                </option>
              ))}
            </select>
          </Campo>

          <Campo
            rotulo="Tipo de anúncio"
            dica="Premium cobra mais e oferece parcelamento sem juros."
          >
            <select
              value={tipoAnuncio}
              onChange={(e) => setTipoAnuncio(e.target.value as TipoAnuncio)}
              className="w-full rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca"
            >
              {TIPOS_ANUNCIO.map((t) => (
                <option key={t} value={t}>
                  {ROTULO_TIPO_ANUNCIO[t]}
                </option>
              ))}
            </select>
          </Campo>

          {/*
            A tarifa do marketplace é consultada, não cadastrada. Aqui a tela
            mostra QUAL número está usando e de onde ele veio — sem isso, o
            resultado sairia certo e ninguém saberia dizer por quê.
          */}
          {produtoId && tarifaResolvida.definida && (
            <p className="rounded border-l-3 border-c-via bg-c-via/10 px-3 py-2 text-xs text-tinta-2">
              O Mercado Livre cobra{' '}
              <strong className="text-tinta">
                {formatarPct(tarifaResolvida.tarifa.percentualBps)}
              </strong>
              {tarifaResolvida.tarifa.fixoCents > 0 && (
                <>
                  {' '}
                  + <strong className="text-tinta">
                    {formatarReais(tarifaResolvida.tarifa.fixoCents)}
                  </strong>{' '}
                  de taxa fixa
                </>
              )}{' '}
              no {ROTULO_TIPO_ANUNCIO[tipoAnuncio]} para{' '}
              <strong className="text-tinta">{categoria?.nome}</strong> a este preço.{' '}
              <span className="text-tinta-3">
                A faixa muda com o preço — mexa no preço de venda e este número pode mudar.
              </span>
            </p>
          )}

          {/*
            O frete calculado precisa aparecer com o PESO que o gerou. Sem isso,
            um produto leve pagando faixa de 4 kg vira mistério — e o cubado e
            exatamente o caso em que o numero surpreende.
          */}
          {produtoId && frete?.definido && (
            <p className="rounded border-l-3 border-c-via bg-c-via/10 px-3 py-2 text-xs text-tinta-2">
              Custo dos Envios: <strong className="text-tinta">{formatarReais(frete.custoCents)}</strong>{' '}
              na faixa de{' '}
              <strong className="text-tinta">
                {(frete.pesoConsideradoGramas / 1000).toLocaleString('pt-BR')} kg
              </strong>
              {frete.cubadoMandou && (
                <span className="text-c-res"> (peso cubado — o volume manda, não a balança)</span>
              )}
              {frete.limitadoPelaMetade && (
                <span className="text-c-res">
                  {' '}
                  · limitado à metade do preço, porque o produto custa menos de R$ 19
                </span>
              )}
              <span className="mt-0.5 block text-tinta-3">
                Tabela de {TABELA_FRETE_ML.vigenteDesde.split('-').reverse().join('/')}, conferida
                em {TABELA_FRETE_ML.conferidaEm.split('-').reverse().join('/')} — não vem da API,
                então confirme no painel se estiver antiga.
              </span>
            </p>
          )}

          {/*
            Sem preço de venda o frete nem é calculado — e antes a tela ficava
            MUDA nesse caso, sem dizer que decidiu não agir. Regra que decide não
            fazer algo tem que aparecer.
          */}
          {produtoId && precoVendaCents === null && (
            <p className="rounded border-l-3 border-tinta-3 bg-tinta-3/8 px-3 py-2 text-xs text-tinta-2">
              Informe o preço de venda para o sistema consultar a tarifa do Mercado Livre e calcular
              o Custo dos Envios. Os dois dependem do preço.
            </p>
          )}

          {produtoId && frete && !frete.definido && (
            <p className="rounded border-l-3 border-c-res bg-c-res/10 px-3 py-2 text-xs text-tinta-2">
              {RECADO_SEM_FRETE[frete.motivo]}{' '}
              {frete.motivo === 'SEM_PESO' && (
                <Link
                  to={`/mercadorias/${produtoId}`}
                  className="text-marca underline-offset-2 hover:underline"
                >
                  Preencher peso e medidas
                </Link>
              )}
            </p>
          )}

          {produtoId && rateio.definido && (
            <p className="rounded border-l-3 border-c-via bg-c-via/10 px-3 py-2 text-xs text-tinta-2">
              Custo fixo rateado:{' '}
              <strong className="text-tinta">{formatarReais(rateio.porUnidadeCents)}</strong> por
              unidade —{' '}
              {formatarReais(rateio.totalMensalCents)} por mês ÷ {rateio.vendasMensaisEsperadas}{' '}
              vendas esperadas.{' '}
              <span className="text-tinta-3">
                Vender metade disso dobra este valor: é premissa, não medição.
              </span>
            </p>
          )}

          {produtoId && !rateio.definido && (
            <p className="rounded border-l-3 border-c-res bg-c-res/10 px-3 py-2 text-xs text-tinta-2">
              {RECADO_SEM_RATEIO[rateio.motivo]}{' '}
              <Link to="/configuracoes" className="text-marca underline-offset-2 hover:underline">
                Abrir Configurações
              </Link>
            </p>
          )}

          {produtoId && consultandoTarifa && (
            <p className="rounded border-l-3 border-est bg-est/10 px-3 py-2 text-xs text-tinta-2">
              Consultando a tarifa do Mercado Livre para este preço…
            </p>
          )}

          {produtoId && !tarifaResolvida.definida && !consultandoTarifa && (
            <p className="rounded border-l-3 border-c-res bg-c-res/10 px-3 py-2 text-xs text-tinta-2">
              {erroTarifa ?? RECADO_SEM_TARIFA[tarifaResolvida.motivo]}{' '}
              {tarifaResolvida.motivo === 'SEM_CATEGORIA' && (
                <Link
                  to={`/mercadorias/${produtoId}`}
                  className="text-marca underline-offset-2 hover:underline"
                >
                  Abrir a ficha da mercadoria
                </Link>
              )}
              {tarifaResolvida.motivo === 'CATEGORIA_SEM_VINCULO' && (
                <Link to="/configuracoes" className="text-marca underline-offset-2 hover:underline">
                  Abrir Configurações
                </Link>
              )}
            </p>
          )}

          {produtoId && (
            <Campo
              rotulo="De onde vem o custo"
              dica={
                respostas.length === 0
                  ? 'Nenhum fornecedor cotou este item — informe o preço à mão.'
                  : 'A cotação mais barata já vem escolhida.'
              }
            >
              <select
                value={respostaId}
                onChange={(e) => setRespostaId(e.target.value)}
                className="w-full rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca"
              >
                <option value="">Informar o preço à mão</option>
                {respostas
                  .filter((r) => r.precoUnitarioCents !== null)
                  .map((r) => (
                    <option key={`${r.cotacaoId}-${r.id}`} value={`${r.cotacaoId}-${r.id}`}>
                      {nomeFornecedor[r.fornecedorId] ?? 'fornecedor'} —{' '}
                      {formatarReais(r.precoUnitarioCents!)}
                    </option>
                  ))}
              </select>
            </Campo>
          )}

          {produtoId && !resposta && (
            <CampoReais
              rotulo="Preço do fornecedor por unidade"
              cents={precoFornecedorManualCents}
              aoMudar={setPrecoFornecedorManualCents}
            />
          )}

          <CampoQuantidade
            rotulo="Quantidade a comprar"
            milli={quantidadeMilli}
            aoMudar={setQuantidadeMilli}
            dica={produto ? `em ${produto.unidadeMedida}` : undefined}
          />

          <CampoReais
            rotulo="Preço de venda por unidade"
            cents={precoVendaCents}
            aoMudar={setPrecoVendaCents}
            dica="Informado à mão: a pesquisa de mercado entra na S7."
          />

          <Campo rotulo="Perfil de custo">
            <select
              value={perfilId}
              onChange={(e) => setPerfilId(e.target.value)}
              disabled={semPerfil}
              className="w-full rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca disabled:opacity-60"
            >
              {perfis.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                  {p.padrao ? ' (padrão)' : ''}
                </option>
              ))}
            </select>
          </Campo>

          <details className="rounded border border-regua-2 px-3 py-2">
            <summary className="cursor-pointer text-xs font-medium text-tinta-2">
              Outros custos para receber a mercadoria
            </summary>
            <div className="mt-3 flex flex-col gap-3">
              {(
                [
                  ['freteEntradaCents', 'Frete de entrada'],
                  ['impostosAquisicaoCents', 'Impostos na compra'],
                  ['seguroCents', 'Seguro'],
                  ['inspecaoCents', 'Inspeção'],
                  ['armazenagemEntradaCents', 'Armazenagem de entrada'],
                  ['outrosAquisicaoCents', 'Outros'],
                ] as [keyof ExtrasAquisicao, string][]
              ).map(([campo, rotulo]) => (
                <CampoReais
                  key={campo}
                  rotulo={rotulo}
                  cents={extras[campo]}
                  aoMudar={(v) => setExtras((x) => ({ ...x, [campo]: v ?? 0 }))}
                />
              ))}
              <p className="text-[0.7rem] text-tinta-3">
                Valores do lote inteiro, não por unidade.
              </p>
            </div>
          </details>

          <CampoPercentual
            rotulo="Perda e avaria"
            bps={perfil?.perdaBps ?? 0}
            aoMudar={() => {}}
            desabilitado
            dica="Vem do perfil de custo."
          />
        </form>

        {/* ---------- resultado ---------- */}
        <div className="flex flex-col gap-4">
          {!entrada || !resultado || !veredito ? (
            <Vazio
              icone={<Calculator className="size-8" />}
              titulo="Faltam dados para a conta"
              descricao="Escolha a mercadoria, a quantidade, o custo do fornecedor e o preço de venda. O resultado aparece aqui e se refaz a cada mudança."
            />
          ) : (
            <>
              <CartaoClassificacao valor={veredito.classificacao} motivos={veredito.motivos} />

              {/*
                O botão só aparece com a conta fechada. Guardar uma análise
                incompleta criaria uma oportunidade cujo lucro é indefinido — e
                ela entraria no total do painel como se fosse decisão real.
              */}
              {podeEscrever && resultado.completo && (
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    disabled={guardando || !!guardada}
                    onClick={guardarOportunidade}
                    className="inline-flex items-center gap-1.5 rounded bg-marca px-4 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90 disabled:opacity-60"
                  >
                    <Target className="size-4" aria-hidden />
                    {guardando ? 'Guardando…' : 'Guardar como oportunidade'}
                  </button>
                  {guardada && (
                    <span className="text-xs text-c-via">
                      Guardada.{' '}
                      <Link to="/oportunidades" className="underline underline-offset-2">
                        Ver em Oportunidades
                      </Link>
                    </span>
                  )}
                  {erroGuardar && <span className="text-xs text-pend">{erroGuardar}</span>}
                </div>
              )}

              <GradeIndicadores>
                <Indicador
                  rotulo="Custo unitário efetivo"
                  valor={formatarReais(resultado.custoUnitarioEfetivoCents)}
                  rodape="já com perdas e custos de entrada"
                />
                <Indicador
                  rotulo="Custo total da compra"
                  valor={formatarReais(resultado.custoTotalAquisicaoCents)}
                />
                {resultado.completo ? (
                  <>
                    <Indicador
                      rotulo="Lucro total"
                      valor={formatarReais(resultado.lucroTotalCents)}
                      alerta={resultado.lucroTotalCents < 0}
                    />
                    <Indicador rotulo="Margem" valor={formatarPct(resultado.margemBps)} />
                    <Indicador rotulo="ROI" valor={formatarPct(resultado.roiBps)} />
                    <Indicador
                      rotulo="Capital necessário"
                      valor={formatarReais(resultado.capitalNecessarioCents)}
                      rodape="o que sai do bolso antes de vender"
                    />
                    <Indicador
                      rotulo="Preço de equilíbrio"
                      valor={
                        resultado.precoEquilibrioCents === null ? (
                          <span className="text-base font-normal text-tinta-3 italic">
                            não existe
                          </span>
                        ) : (
                          formatarReais(resultado.precoEquilibrioCents)
                        )
                      }
                      rodape={
                        resultado.precoEquilibrioCents === null
                          ? 'os custos percentuais consomem toda a receita'
                          : 'abaixo disto, prejuízo'
                      }
                    />
                    <Indicador
                      rotulo="Margem de segurança"
                      valor={
                        resultado.margemSegurancaBps === null ? (
                          <span className="text-base font-normal text-tinta-3 italic">n/i</span>
                        ) : (
                          formatarPct(resultado.margemSegurancaBps)
                        )
                      }
                      rodape="quanto o preço pode cair"
                    />
                  </>
                ) : (
                  <div className="col-span-2 flex items-start gap-2 rounded-md border border-dashed border-regua-2 bg-superficie px-3 py-2.5 text-sm text-tinta-2 sm:col-span-3 xl:col-span-4">
                    <Info className="mt-0.5 size-4 shrink-0 text-c-dad" aria-hidden />
                    <span>
                      Margem, ROI e capital não aparecem porque falta{' '}
                      <strong className="text-tinta">{resultado.faltando.join(', ')}</strong>. Não é
                      travamento: o sistema se recusa a chamar de zero um número que ninguém
                      informou.{' '}
                      <Link
                        to="/configuracoes"
                        className="text-marca underline-offset-2 hover:underline"
                      >
                        Complete o perfil de custo
                      </Link>
                      .
                    </span>
                  </div>
                )}
              </GradeIndicadores>

              {cenarios.length > 0 && <TabelaCenarios cenarios={cenarios} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const ROTULO_CENARIO = {
  PESSIMISTA: 'Pessimista',
  PROVAVEL: 'Provável',
  OTIMISTA: 'Otimista',
} as const;

/**
 * Os três cenários lado a lado.
 *
 * O que importa aqui não é o provável — é o pessimista. Uma operação que só se
 * paga no cenário bom é uma aposta, e a tabela existe para isso ficar visível
 * antes da compra, não depois.
 */
function TabelaCenarios({ cenarios }: { cenarios: Cenario[] }) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="font-mono text-[0.62rem] tracking-wider text-marca uppercase">Cenários</h2>
      <div className="overflow-x-auto rounded-md border border-regua-2">
        <table className="w-full min-w-[32rem] border-collapse bg-superficie text-sm">
          <thead>
            <tr>
              {['Cenário', 'Preço de venda', 'Vendido', 'Lucro total', 'Margem', 'ROI'].map((h) => (
                <th
                  key={h}
                  className="border-b border-regua-2 px-3 py-2 text-left font-mono text-[0.62rem] font-semibold tracking-wider text-marca uppercase"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cenarios.map((c) => (
              <tr key={c.tipo} className="border-b border-regua last:border-0 even:bg-marca/4">
                <td className="px-3 py-2 font-medium">{ROTULO_CENARIO[c.tipo]}</td>
                <td className="px-3 py-2 text-right">
                  <Reais cents={c.precoVendaCents} />
                </td>
                <td className="px-3 py-2 text-right num">{formatarPct(c.pctVendidoBps)}</td>
                <td className="px-3 py-2 text-right">
                  {c.resultado.completo ? (
                    <span className={c.resultado.lucroTotalCents < 0 ? 'text-pend' : ''}>
                      <Reais cents={c.resultado.lucroTotalCents} />
                    </span>
                  ) : (
                    <span className="text-xs text-tinta-3 italic">sem conta</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right num">
                  {c.resultado.completo ? formatarPct(c.resultado.margemBps) : '—'}
                </td>
                <td className="px-3 py-2 text-right num">
                  {c.resultado.completo ? formatarPct(c.resultado.roiBps) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
